import React, {ReactElement, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useCookies} from 'react-cookie';
import {cx} from '@emotion/css';
import Button, {ButtonVariant} from '../DesignSystem/Buttons/Button/Button';
import DynamicLink from '../DynamicLink/DynamicLink';
import {cookieDomain, hasSecureCookie, CONSENT_COOKIE_NAME} from '../../constants';
import {ReactComponent as FundingOptionsLogoWithText} from '../../images/fundingOptionsLogoWithTextYellow.svg';
import BaseModal from '../DesignSystem/BaseModal/BaseModal';
import Paragraph, {FontWeight} from '../DesignSystem/Text/Paragraph/Paragraph';
import Headers, {HeaderType} from '../DesignSystem/Text/Headers/Headers';
import {Alignment} from '../../types';

import styles from './TermsModal.styles';

function TermsModal(): ReactElement {
  const {formatMessage} = useIntl();
  const [_cookies, setCookie] = useCookies([CONSENT_COOKIE_NAME]);
  const [shouldShowMore, setShowMore] = useState<boolean>(false);

  const handleClose = (baseModalOnClose: () => void, val?: 'true' | 'temp') => () => {
    setCookie(CONSENT_COOKIE_NAME, val || true, {
      path: '/',
      domain: cookieDomain,
      secure: hasSecureCookie,
    });

    if (baseModalOnClose) {
      baseModalOnClose();
    }
  };

  return (
    <BaseModal showTopCloseButton={false}>
      {(onClose) => (
        <div className={cx(styles.termsModal(shouldShowMore))} data-testid="modal.terms">
          {shouldShowMore ? (
            <>
              <FundingOptionsLogoWithText className={cx(styles.termsModalLogo)} />
              <Headers headerType={HeaderType.H3} alignment={Alignment.CENTER}>
                <FormattedMessage id="cookies.modal.more.title" />
              </Headers>
              <Paragraph alignment={Alignment.CENTER} fontWeight={FontWeight.REGULAR}>
                <FormattedMessage
                  id="cookies.modal.more.copy"
                  values={{
                    'a-cookies': (...chunks: string[]) => (
                      <DynamicLink onClick={handleClose(onClose, 'temp')} href="/terms#CookiePolicy">
                        {chunks}
                      </DynamicLink>
                    ),
                    'a-privacy': (...chunks: string[]) => (
                      <DynamicLink onClick={handleClose(onClose, 'temp')} href="/terms#PrivacyPolicy">
                        {chunks}
                      </DynamicLink>
                    ),
                  }}
                />
              </Paragraph>
            </>
          ) : (
            <>
              <Headers headerType={HeaderType.H3} alignment={Alignment.CENTER}>
                <FormattedMessage id="cookies.modal.title" />
              </Headers>
              <Paragraph alignment={Alignment.CENTER} fontWeight={FontWeight.REGULAR}>
                <FormattedMessage id="cookies.modal.copy" />
              </Paragraph>
              <Paragraph alignment={Alignment.CENTER} fontWeight={FontWeight.REGULAR}>
                <FormattedMessage
                  id="cookies.modal.more"
                  values={{
                    button: (...chunks: string[]) => (
                      <Button
                        type="button"
                        variant={ButtonVariant.LINK_TEXT}
                        ariaLabel={chunks[0]}
                        onClick={() => setShowMore(true)}
                      >
                        {chunks}
                      </Button>
                    ),
                  }}
                />
              </Paragraph>
            </>
          )}
          <Button
            type="button"
            variant={ButtonVariant.PRIMARY}
            ariaLabel={formatMessage({id: 'cookies.modal.cta'})}
            onClick={handleClose(onClose, 'true')}
            testId="button.termsModal.close"
          >
            <FormattedMessage id="cookies.modal.cta" />
          </Button>
        </div>
      )}
    </BaseModal>
  );
}

export default TermsModal;
